/**
 * Caro core FX — the rotating TURRET HUB the laser carousel (family `caro`) fires
 * out of, so its red beams read as spinning out of a real emitter rather than
 * floating from nothing. *Core decides, Phaser renders*: core owns the beam
 * geometry/spin; this paints, at the carousel centre:
 *   - a soft pulsing teal ENERGY GLOW (under the beams),
 *   - a steel CORE disc (over the barrel bases), and
 *   - ONE BARREL per live beam, pivoted at the hub centre and rotated to that beam's
 *     heading — so for 1/2/3/4 beams the emitter shows 1/2/3/4 barrels and the beams
 *     ALWAYS exit a barrel (a 3-beam caro is a clean 3-arm emitter, not a stray Y).
 *
 * Decorative + pooled, one set (glow + core + up to four barrels) per caro id.
 */
import Phaser from 'phaser';
import { HAZARD_TEX, HAZARD_COLORS } from './textures';

const GLOW_DEPTH = 4; // energy glow under the beams (hazards draw at 5)
const BARREL_DEPTH = 6; // barrels over the beams so the beams appear to leave them
const CORE_DEPTH = 7; // core disc over the barrel bases
const SPARK_TEX = 32;
const CORE_TEX = 64;
const BARREL_TEX_W = 32;
const BARREL_TEX_H = 72;
const MAX_BEAMS = 4;

interface CState {
  glow: Phaser.GameObjects.Image;
  core: Phaser.GameObjects.Image;
  /** Always {@link MAX_BEAMS} sprites; only the first `beams` are shown each update. */
  barrels: Phaser.GameObjects.Image[];
}

export class CaroFx {
  private readonly scene: Phaser.Scene;
  private readonly freeGlow: Phaser.GameObjects.Image[] = [];
  private readonly freeCore: Phaser.GameObjects.Image[] = [];
  private readonly freeBarrel: Phaser.GameObjects.Image[] = [];
  private readonly all: Phaser.GameObjects.Image[] = [];
  private readonly state = new Map<number, CState>();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Paint the caro emitter at `(cx,cy)`, sized off the beam `length`. `angle0` is the
   * carousel's current spin angle (the first beam's heading) and `beams` the live beam
   * count — one barrel is drawn per beam, aligned to `angle0 + i·(2π/beams)`.
   */
  update(
    id: number,
    cx: number,
    cy: number,
    length: number,
    angle0: number,
    beams: number,
    elapsedMs: number,
    active: boolean,
  ): void {
    let st = this.state.get(id);
    if (!st) {
      st = {
        glow: this.acquire(this.freeGlow, HAZARD_TEX.spark, GLOW_DEPTH),
        core: this.acquire(this.freeCore, HAZARD_TEX.caroCore, CORE_DEPTH),
        barrels: Array.from({ length: MAX_BEAMS }, () =>
          this.acquire(this.freeBarrel, HAZARD_TEX.caroBarrel, BARREL_DEPTH),
        ),
      };
      this.state.set(id, st);
    }

    const pulse = 0.5 + 0.5 * Math.sin(elapsedMs / 120);
    const hubDia = Math.max(48, length * 0.084);

    // Soft energy glow under everything.
    st.glow
      .setPosition(cx, cy)
      .setVisible(true)
      .setTint(HAZARD_COLORS.turretBody)
      .setScale((hubDia * 1.5) / SPARK_TEX)
      .setAlpha((active ? 0.4 : 0.22) + 0.14 * pulse);

    // Steel core disc (slowly spinning), over the barrel bases.
    st.core
      .setPosition(cx, cy)
      .setVisible(true)
      .setRotation(elapsedMs / 1400)
      .setScale((hubDia * 0.92) / CORE_TEX * (0.97 + 0.03 * pulse))
      .setAlpha(1);

    // One barrel per live beam, pivoted at the hub centre and aimed along each beam.
    const n = Math.max(1, Math.min(MAX_BEAMS, beams));
    const step = (Math.PI * 2) / n;
    const barrelLen = hubDia * 0.62;
    const barrelW = hubDia * 0.26;
    for (let i = 0; i < MAX_BEAMS; i++) {
      const b = st.barrels[i]!;
      if (i < n) {
        const ang = angle0 + i * step;
        b.setVisible(true)
          .setOrigin(0.5, 1) // pivot at the base = the hub centre
          .setPosition(cx, cy)
          // Art points UP (-y); +90° aligns it to the beam's screen heading `ang`.
          .setRotation(ang + Math.PI / 2)
          .setScale(barrelW / BARREL_TEX_W, barrelLen / BARREL_TEX_H)
          .setAlpha(active ? 1 : 0.9);
      } else {
        b.setVisible(false);
      }
    }
  }

  sweep(alive: Set<number>): void {
    for (const [id, st] of this.state) {
      if (alive.has(id)) continue;
      this.release(st);
      this.state.delete(id);
    }
  }

  reset(): void {
    for (const st of this.state.values()) this.release(st);
    this.state.clear();
  }

  destroy(): void {
    this.state.clear();
    for (const s of this.all) s.destroy();
    this.all.length = 0;
    this.freeGlow.length = 0;
    this.freeCore.length = 0;
    this.freeBarrel.length = 0;
  }

  private acquire(
    free: Phaser.GameObjects.Image[],
    tex: string,
    depth: number,
  ): Phaser.GameObjects.Image {
    const s = free.pop() ?? this.make(tex, depth);
    s.setActive(true).setVisible(true);
    return s;
  }

  private release(st: CState): void {
    const reset = (s: Phaser.GameObjects.Image): void => {
      s.setActive(false).setVisible(false).clearTint().setAlpha(1).setAngle(0).setScale(1).setOrigin(0.5, 0.5);
    };
    reset(st.glow);
    this.freeGlow.push(st.glow);
    reset(st.core);
    this.freeCore.push(st.core);
    for (const b of st.barrels) {
      reset(b);
      this.freeBarrel.push(b);
    }
  }

  private make(tex: string, depth: number): Phaser.GameObjects.Image {
    // The spark glow is ADD-blended energy; the core + barrels are solid art (NORMAL).
    const blend = tex === HAZARD_TEX.spark ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL;
    const s = this.scene.add.image(0, 0, tex).setDepth(depth).setBlendMode(blend);
    this.all.push(s);
    return s;
  }
}
